import { X } from "lucide-react";
import { type ChangeEvent, type FC, useRef } from "react";
import { type UseFormReturn } from "react-hook-form";

import { posIntegrationApi } from "@/api/admin/posIntegration";
import { CustomButton } from "@/components/atoms";
import { toastError } from "@/components/molecules";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { downloadBlob } from "@/utils/downloadBlob";

import type { BranchPos } from "../types";
import {
  downloadTableIdMappingTemplate,
  resolveTableIdMappingFileName,
  TABLE_ID_MAPPING_MAX_BYTES,
} from "../utils/tableIdMappingCsv";

interface Props {
  form: UseFormReturn<BranchPos>;
  branchId: string;
  disabled?: boolean;
}

const TableIdMappingField: FC<Props> = ({ form, branchId, disabled }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFile = form.watch("tableIdMappingFile");
  const savedFileName = form.watch("tableIdMappingFileName");
  const removed = form.watch("tableIdMappingRemoved");
  const rawName =
    pendingFile?.name ?? (!removed ? savedFileName : null) ?? null;
  const displayName = rawName
    ? resolveTableIdMappingFileName(pendingFile?.name, savedFileName)
    : null;

  const handlePickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const retryPick = () => fileInputRef.current?.click();
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toastError("Only a CSV file is allowed (max 1 MB)", retryPick);
      return;
    }
    if (file.size > TABLE_ID_MAPPING_MAX_BYTES) {
      toastError("Only a CSV file is allowed (max 1 MB)", retryPick);
      return;
    }

    const fileName = resolveTableIdMappingFileName(file.name);
    form.setValue("tableIdMappingFile", file, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue("tableIdMappingFileName", fileName, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue("tableIdMappingRemoved", false, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleRemove = () => {
    form.setValue("tableIdMappingFile", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue("tableIdMappingRemoved", true, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleDownload = async () => {
    try {
      const fileName = resolveTableIdMappingFileName(
        pendingFile?.name,
        savedFileName,
        displayName
      );
      if (pendingFile) {
        downloadBlob(pendingFile, fileName);
        return;
      }
      if (!savedFileName && !displayName) return;
      const { blob, filename } =
        await posIntegrationApi.downloadTableIdMapping(branchId);
      downloadBlob(
        blob,
        resolveTableIdMappingFileName(savedFileName, filename)
      );
    } catch {
      toastError("Could not download the table ID mapping.", () => {
        void handleDownload();
      });
    }
  };

  return (
    <FormField
      control={form.control}
      name="tableIdMappingFile"
      render={() => (
        <FormItem className="flex flex-col gap-2 mt-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="table-id-mapping-file-input"
            disabled={disabled}
            onChange={handlePickFile}
          />

          {displayName ? (
            <div
              className="flex items-center gap-2 max-w-md"
              data-testid="table-id-mapping-file-chip"
            >
              <button
                type="button"
                className="truncate text-sm font-medium text-[#155FCC] underline-offset-4 hover:underline cursor-pointer disabled:opacity-50"
                data-testid="table-id-mapping-download"
                aria-label={`Download ${displayName}`}
                disabled={disabled}
                onClick={() => {
                  void handleDownload();
                }}
              >
                {displayName}
              </button>
              <button
                type="button"
                className="p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                data-testid="table-id-mapping-remove"
                aria-label="Remove table ID mapping"
                disabled={disabled}
                onClick={handleRemove}
              >
                <X className="size-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <CustomButton
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                data-testid="upload-table-ids-button"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload table IDs
              </CustomButton>
              <CustomButton
                type="button"
                variant="link"
                className="h-auto p-0"
                disabled={disabled}
                data-testid="table-id-mapping-template"
                onClick={downloadTableIdMappingTemplate}
              >
                Template
              </CustomButton>
            </div>
          )}

          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default TableIdMappingField;
